import { useMemo, useState } from "react";
import { addDays, format, startOfWeek, isToday } from "date-fns";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Settings2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading } from "@/components/common";
import { FormDialog, NONE } from "@/components/FormDialog";
import { ViewingSettingsDialog } from "@/components/ViewingSettingsDialog";
import { ViewingDetailDialog, VIEW_STATUS, whenText } from "@/components/ViewingDetailDialog";
import { api, useFetch, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const CHIP = { requested: "border-amber-300 bg-amber-50", confirmed: "border-emerald-300 bg-emerald-50", completed: "border-slate-200 bg-slate-50", declined: "border-slate-200 bg-white opacity-60 line-through", cancelled: "border-slate-200 bg-white opacity-60 line-through", no_show: "border-rose-200 bg-rose-50" };

function Requests({ items, onOpen, onChanged }) {
  const quick = async (v, status) => {
    try { await api.patch(`/biz/viewings/${v.id}`, { status }); toast.success(status === "confirmed" ? "Viewing approved" : "Request declined"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!items.length) return null;
  return (
    <div className="panel mb-6 border-amber-200" data-testid="viewing-requests">
      <h3 className="text-lg font-bold mb-3">Awaiting your approval ({items.length})</h3>
      <div className="divide-y divide-slate-100">
        {items.map((v) => (
          <div key={v.id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`viewing-request-${v.id}`}>
            <button onClick={() => onOpen(v)} className="text-left flex-1 min-w-0">
              <div className="font-semibold">{whenText(v)} · {v.name}</div>
              <div className="text-xs text-slate-500 truncate">{v.listing_title} {v.message && `— “${v.message}”`}</div>
            </button>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => quick(v, "confirmed")} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`approve-${v.id}`}><Check className="w-4 h-4 mr-1" />Approve</Button>
              <Button size="sm" variant="outline" onClick={() => quick(v, "declined")} data-testid={`decline-${v.id}`}><X className="w-4 h-4 mr-1" />Decline</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Week({ start, items, onOpen }) {
  const days = [...Array(7)].map((_, i) => addDays(start, i));
  return (
    <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="viewings-week">
      {days.map((d) => {
        const key = format(d, "yyyy-MM-dd");
        const list = items.filter((v) => v.start.startsWith(key));
        return (
          <div key={key} className={cn("rounded-xl border bg-white p-3 md:min-h-[140px]", isToday(d) ? "border-emerald-600" : "border-slate-200")} data-testid={`day-${key}`}>
            <div className="flex md:flex-col items-baseline md:items-start gap-2 mb-2">
              <span className="text-xs uppercase tracking-wider text-slate-500">{format(d, "EEE")}</span>
              <span className={cn("font-display text-xl font-extrabold", isToday(d) && "text-emerald-700")}>{format(d, "d MMM")}</span>
            </div>
            <div className="space-y-2">
              {list.map((v) => (
                <button key={v.id} onClick={() => onOpen(v)} className={cn("w-full text-left rounded-lg border px-2 py-1.5 text-xs transition-transform hover:-translate-y-0.5", CHIP[v.status])} data-testid={`viewing-chip-${v.id}`}>
                  <div className="font-bold">{v.start.slice(11)} · {VIEW_STATUS[v.status]?.[1]}</div>
                  <div className="truncate">{v.listing_title || v.title}</div>
                  <div className="truncate text-slate-500">{v.name}</div>
                </button>
              ))}
              {!list.length && <div className="text-xs text-slate-300">—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Viewings() {
  const { session } = useAuth();
  const isOwner = session.user.id === session.business.owner_user_id;
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const range = `start=${format(weekStart, "yyyy-MM-dd")}&end=${format(addDays(weekStart, 6), "yyyy-MM-dd")}`;
  const { data, reload } = useFetch(`/biz/viewings?${range}`);
  const { data: pending, reload: reloadPending } = useFetch("/biz/viewings?status=requested");
  const { data: listings } = useFetch("/biz/listings");
  const [sel, setSel] = useState(null);
  const [adding, setAdding] = useState(false);
  const [settings, setSettings] = useState(false);
  const refresh = () => { reload(); reloadPending(); };
  const upcoming = useMemo(() => (pending || []).filter((v) => v.end >= format(new Date(), "yyyy-MM-dd'T'HH:mm")), [pending]);
  return (
    <div data-testid="viewings-page">
      <PageHeader eyebrow="Calendar" title="Viewings" subtitle="Approve viewing requests from your property pages and keep every appointment in one calendar."
        actions={<>
          <Button variant="outline" onClick={() => setSettings(true)} data-testid="viewing-settings-button"><Settings2 className="w-4 h-4 mr-1" />Availability & calendar</Button>
          <Button onClick={() => setAdding(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-viewing-button"><Plus className="w-4 h-4 mr-1" />Add appointment</Button>
        </>} />
      <Requests items={upcoming} onOpen={setSel} onChanged={refresh} />
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="week-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="week-next"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))} data-testid="week-today">Today</Button>
        </div>
        <div className="font-semibold flex items-center gap-2" data-testid="week-label"><CalendarDays className="w-4 h-4 text-emerald-700" />{format(weekStart, "d MMM")} – {format(addDays(weekStart, 6), "d MMM yyyy")}</div>
      </div>
      {!data ? <Loading /> : <Week start={weekStart} items={data} onOpen={setSel} />}
      <ViewingDetailDialog v={sel} onClose={() => setSel(null)} onChanged={refresh} />
      <ViewingSettingsDialog open={settings} onOpenChange={setSettings} disabled={!isOwner} />
      <FormDialog open={adding} onOpenChange={setAdding} title="Add appointment" description="Booked straight into your calendar as confirmed." testid="add-viewing-form" submitLabel="Add to calendar"
        fields={[
          { name: "listing_id", label: "Listing", type: "select", nullable: true, noneLabel: "No listing (other appointment)", full: true, options: (listings || []).map((x) => ({ value: x.id, label: x.title })) },
          { name: "title", label: "Title (if no listing)", full: true },
          { name: "start", label: "Date & time", type: "datetime-local", required: true },
          { name: "duration_minutes", label: "Length (minutes)", type: "number", default: 30, step: "5" },
          { name: "name", label: "Visitor name", required: true },
          { name: "phone", label: "Phone" },
          { name: "email", label: "Email", type: "email", full: true },
          { name: "notes", label: "Notes", type: "textarea" },
        ]}
        onSubmit={async (v) => {
          const r = await api.post("/biz/viewings", { ...v, listing_id: v.listing_id === NONE ? null : v.listing_id, email: v.email || null });
          toast.success(r.data.clash ? "Added — note it overlaps another appointment" : "Appointment added");
          setWeekStart(startOfWeek(new Date(r.data.start), { weekStartsOn: 1 }));
          refresh();
        }} />
    </div>
  );
}
